import { useContext, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { EntityContext } from 'context/entityContext';
import getAllowedCustomerFilters from 'helpers/getAllowedCustomerFilters';
import getCustomerFilters from 'helpers/getCustomerFilters';
import { TAllowedEntityTypes } from 'helpers/getAllowedCustomerFilters';

import {
  loadCustomers,
  selectCustomersByFeedType,
  isCustomersLoaded,
} from 'redux/reducers/customers';

export default useCustomerFilters;

/**
 * This hook returns the filter types you are allowed to use for this salesType,
 * as well as the correct argument to use for your API request query parameter.
 *
 * @param {String} salesType The sales type you need to know the allowed + current filters for
 * @returns {Array} [allowedFilters, customerFilter]
 */
function useCustomerFilters(salesType: any): any {
  const { entityType, filters } = useContext(EntityContext);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCustomers());
  });

  const memoizedSelectCustomersByFeedType = useCallback(
    selectCustomersByFeedType(['streaming', 'downloads']),
    []
  );

  const customers = useSelector(memoizedSelectCustomersByFeedType);
  const isCustomersDataLoaded = useSelector(isCustomersLoaded);

  const allowedFilters = useMemo(
    () =>
      getAllowedCustomerFilters(
        typeof salesType === 'string' ? salesType : salesType.value,
        entityType as TAllowedEntityTypes
      ),
    [salesType, entityType]
  );

  const customerFilter = useMemo(() => {
    if (Array.isArray(customers) && customers.length > 0) {
      const filter = getCustomerFilters(customers, allowedFilters, filters);

      return filter;
    }
  }, [allowedFilters, customers, filters]);

  const isFilterApplied = useMemo(() => allowedFilters.some((el: any) => !!(filters as any)?.[el]),
    [allowedFilters, customers, filters]);

  // customer data loaded for a long period of time, and when dsp filter applied it creates a double requests
  const isDspFilterApplied = useMemo(() => isFilterApplied && (filters?.download || filters?.streaming),
    [isFilterApplied, filters]);

  const isFilterLoaded = useMemo(() => !isDspFilterApplied || (isDspFilterApplied && isCustomersDataLoaded),
    [isDspFilterApplied, isCustomersDataLoaded])

  return [allowedFilters, customerFilter, isFilterLoaded, isDspFilterApplied];
}
